import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Package, Truck, CheckCircle2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const iconFor = (text) => {
  const t = (text || "").toLowerCase();
  if (t.includes("delivered")) return CheckCircle2;
  if (t.includes("out for delivery") || t.includes("transit") || t.includes("shipped")) return Truck;
  return Package;
};

const formatDate = (date) =>
  new Date(date).toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  });

const OrderTrackingTimeline = ({ order, queryKey }) => {
  const queryClient = useQueryClient();
  const shipping = order?.shipping;

  const { mutate: refresh, isPending } = useMutation({
    mutationFn: async () => (await api.get(`/orders/${order._id}/tracking`)).data,
    onSuccess: () => {
      if (queryKey) queryClient.invalidateQueries({ queryKey });
      toast.success("Tracking status updated");
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Could not refresh tracking status");
    },
  });

  if (!shipping?.awb_code) {
    return (
      <div className="mt-8 text-center" data-testid="order-tracking">
        <p className="text-[10px] tracking-[0.3em] uppercase text-[#5C5C5C]">Your order is being prepared</p>
      </div>
    );
  }

  const history = [...(shipping.trackingHistory || [])].sort((a, b) => new Date(a.date) - new Date(b.date));

  return (
    <div className="mt-8 w-full max-w-sm text-left" data-testid="order-tracking">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#5C5C5C]">{shipping.courier_name}</p>
          <p className="text-sm text-[#111111]">AWB: {shipping.awb_code}</p>
        </div>
        <button
          onClick={() => refresh()}
          disabled={isPending}
          data-testid="refresh-tracking-btn"
          className="flex items-center gap-1 text-[10px] tracking-[0.25em] uppercase text-[#C07C5F] hover:text-[#111111] transition-colors duration-300 disabled:opacity-50"
        >
          <RefreshCw size={12} className={isPending ? "animate-spin" : ""} />
          {isPending ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {history.length > 0 ? (
        <div className="flex flex-col gap-4 border-l border-[#EBE8E3] pl-4">
          {history.map((checkpoint, i) => {
            const Icon = iconFor(checkpoint.activity || checkpoint.status);
            const isLast = i === history.length - 1;
            return (
              <div key={i} className="relative">
                <div
                  className={`absolute -left-[22px] top-0 w-4 h-4 rounded-full border flex items-center justify-center bg-[#F9F8F6] ${
                    isLast ? "border-[#C07C5F] text-[#C07C5F]" : "border-[#EBE8E3] text-[#5C5C5C]"
                  }`}
                >
                  <Icon size={10} />
                </div>
                <p className={`text-sm ${isLast ? "text-[#111111]" : "text-[#5C5C5C]"}`}>
                  {checkpoint.activity || checkpoint.status}
                </p>
                <p className="text-[10px] uppercase tracking-wide text-[#5C5C5C] mt-0.5">
                  {[checkpoint.location, checkpoint.date ? formatDate(checkpoint.date) : null].filter(Boolean).join(" · ")}
                </p>
              </div>
            );
          })}
        </div>
      ) : (
        shipping.status && <p className="text-sm text-[#5C5C5C]">Latest: {shipping.status}</p>
      )}

      {shipping.trackingUrl && (
        <a
          href={shipping.trackingUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-block mt-4 text-xs text-[#C07C5F] underline"
        >
          View on carrier site
        </a>
      )}
    </div>
  );
};

export default OrderTrackingTimeline;
